/**
 * The level's two timed item slots (spec 02 §7.3): the per-second timers the IRQ counts (irq-handlers.s
 * $06C6: dec $5D / dec $5E while TXTTAB runs) and the main-loop pass that shows or removes an item when
 * its timer goes below 0 (bubbles-sprites.s update_bubbles, $1578). An item is an `Item` in
 * `world.items` only while it is on screen.
 */

import { FOODS } from '../bubbles';
import { POWERUPS } from './powerups';
import { SECOND_FRAMES, SHOWN_TIMER, SLOT_X, SLOT_Y, UNITS_PER_POINT } from './tuning';
import type { ItemSystem, Slot } from './state';
import type { Item, World } from '../types';
import type { TilePos } from '../../levels/types';

/** A slot holding `item`, its timer at `timer` seconds, at the 2x2 item tile `tile` (D_2C8C). */
export function newSlot(item: number, timer: number, tile: TilePos): Slot {
  return { item, visible: false, timer, x: tile.x * 8 + SLOT_X, y: tile.y * 8 + SLOT_Y, itemId: -1 };
}

/** Stored units a slot's item pays (D_A936 for the food, D_A965 for the power-up, x 10 or x 100). */
export function slotUnits(slot: Slot, kind: 'food' | 'powerup'): number {
  const points = kind === 'food' ? FOODS[slot.item]?.points : POWERUPS[slot.item]?.points;
  return Math.round((points ?? 0) / UNITS_PER_POINT);
}

/** Put the slot's item on screen as an `Item`. */
export function showSlot(world: World, slot: Slot, kind: 'food' | 'powerup'): void {
  slot.visible = true;
  const item: Item = {
    id: world.nextId++, kind, item: slot.item, x: slot.x, y: slot.y, value: slotUnits(slot, kind),
    life: slot.timer + 1, falling: false,
  };
  slot.itemId = item.id;
  world.items.push(item);
  world.events.push({ type: 'item', itemId: item.id, spawned: true });
}

/** Empty the slot for good ($FF), taking its item off screen; `expired` raises the item event. */
export function emptySlot(world: World, slot: Slot, expired = false): void {
  if (slot.itemId >= 0) {
    const i = world.items.findIndex((it) => it.id === slot.itemId);
    if (i >= 0) world.items.splice(i, 1);
    if (expired) world.events.push({ type: 'item', itemId: slot.itemId, spawned: false });
  }
  slot.item = -1;
  slot.visible = false;
  slot.itemId = -1;
}

/**
 * Do the per-second timers run? TXTTAB stops under the clock ($2F74) and when the Baron comes ($1621),
 * and starts again at the level clear ($1765); level 100 stops it for good ($16D6).
 */
export function timersRun(world: World): boolean {
  if (world.level.n >= 100 || world.power.clock > 0) return false;
  const f = world.flow;
  if (f.phase === 'clear') return true;
  return f.phase === 'play' && !f.baron;
}

/**
 * Bring the per-second timers up to `flow.frame` (it counts the frames of play, freezes included, as
 * the IRQ does): each 50 frames while they run, both slot timers go down by one.
 */
export function countSeconds(world: World, sys: ItemSystem): void {
  const elapsed = world.flow.frame - sys.frameMark;
  sys.frameMark = world.flow.frame;
  if (elapsed <= 0 || !timersRun(world)) return;
  sys.secondFrames -= elapsed;
  while (sys.secondFrames <= 0) {
    sys.secondFrames += SECOND_FRAMES;
    for (const slot of [sys.food, sys.powerup]) {
      slot.timer--;
      const it = world.items.find((i) => i.id === slot.itemId);
      if (it) it.life = Math.max(0, slot.timer + 1);
    }
  }
}

/**
 * $1578, once per main-loop pass, power-up slot then food: a timer below 0 is set to 11 and the slot
 * flips - hidden to shown, or shown to gone for good.
 */
export function toggleSlots(world: World, sys: ItemSystem): void {
  for (const [slot, kind] of [[sys.powerup, 'powerup'], [sys.food, 'food']] as const) {
    if (slot.timer >= 0) continue;
    slot.timer = SHOWN_TIMER;
    if (slot.item < 0) continue;
    if (!slot.visible) showSlot(world, slot, kind);
    else emptySlot(world, slot, true);
  }
}
